import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

/**
 * Popover root component that controls the popover state.
 * Use with PopoverTrigger and PopoverContent.
 */
const Popover = PopoverPrimitive.Root

/**
 * PopoverTrigger component that opens the popover when clicked.
 * Wraps the element that should trigger the popover.
 */
const PopoverTrigger = PopoverPrimitive.Trigger

/**
 * PopoverAnchor component positions the popover relative to an anchor element.
 */
const PopoverAnchor = PopoverPrimitive.Anchor

/**
 * PopoverContent component displays the popover content panel.
 * Built on Radix UI Popover primitive with animations and positioning.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>} props - Content props
 * @param {"start" | "center" | "end"} [props.align="center"] - Alignment relative to trigger
 * @param {number} [props.sideOffset=4] - Distance from the trigger element
 * @param {React.ReactNode} props.children - Content to display in the popover
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Popover content panel with animations
 * 
 * @example
 * ```tsx
 * <Popover>
 *   <PopoverTrigger>Open</PopoverTrigger>
 *   <PopoverContent>
 *     <p>Popover content</p>
 *   </PopoverContent>
 * </Popover>
 * ```
 */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]",
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
